import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { cn } from '../../../lib/utils';
import { userInfo } from '../../../store/selectors/user';
import { impactOccuer } from '../../../utils/hapticEvents';
import { useGetMindboxUserInfo } from '../../home/services/getMindboxUserInfo';
import { Skeleton } from '../../../components/ui/skeleton';
import coinFullImg from './assets/coin-full.png';
import { EyeIcon } from './assets/EyeIcon';
import { LoyaltyOrnament } from './assets/LoyaltyOrnament';
import { LoyaltyQrIcon } from './assets/LoyaltyQrIcon';
import { LoyaltyQrDrawer } from './LoyaltyQrDrawer';

// Скрытие баланса — на устройстве и навсегда: человек прячет цифру от чужих
// глаз в очереди, и при следующем открытии она не должна выскочить снова.
const BALANCE_HIDDEN_KEY = 'loyaltyBalanceHidden';

const readHidden = () => {
  try {
    return localStorage.getItem(BALANCE_HIDDEN_KEY) === '1';
  } catch {
    return false;
  }
};

const BALANCE_MAX_FONT_PX = 28;
const BALANCE_MIN_FONT_PX = 16;

/**
 * Подбирает размер шрифта баланса под ширину ряда: 28px по макету, а если
 * число вместе с монетой и «глазом» не влезает — уменьшает, чтобы цифры
 * не уходили в многоточие. Ширина текста пропорциональна кеглю, так что
 * хватает одного замера.
 */
const useFitBalanceFont = (text: string) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLParagraphElement>(null);
  const [fontSize, setFontSize] = useState(BALANCE_MAX_FONT_PX);

  useLayoutEffect(() => {
    const row = rowRef.current;
    const textEl = textRef.current;
    if (!row || !textEl) return;

    const fit = () => {
      const styles = getComputedStyle(row);
      const gap = parseFloat(styles.columnGap) || 0;
      // Место под число: ряд минус монета, «глаз» (с их отрицательными
      // полями) и промежутки между элементами.
      let available = row.clientWidth - gap * (row.children.length - 1);
      for (const child of Array.from(row.children)) {
        if (child === textEl) continue;
        const childStyles = getComputedStyle(child);
        available -=
          child.getBoundingClientRect().width +
          (parseFloat(childStyles.marginLeft) || 0) +
          (parseFloat(childStyles.marginRight) || 0);
      }

      const currentSize = parseFloat(getComputedStyle(textEl).fontSize) || BALANCE_MAX_FONT_PX;
      const widthAtMax = (textEl.scrollWidth / currentSize) * BALANCE_MAX_FONT_PX;
      if (!widthAtMax || available <= 0) return;

      const next = Math.floor(
        Math.min(BALANCE_MAX_FONT_PX, Math.max(BALANCE_MIN_FONT_PX, (BALANCE_MAX_FONT_PX * available) / widthAtMax))
      );
      setFontSize(prev => (prev === next ? prev : next));
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(row);
    // Inter Tight может догрузиться позже — тогда ширина цифр меняется.
    document.fonts?.ready.then(fit).catch(() => {});
    return () => observer.disconnect();
  }, [text]);

  return { rowRef, textRef, fontSize };
};

type LoyaltyCardProps = {
  className?: string;
};

/**
 * Карта лояльности — вход в QR с главной и из профиля.
 *
 * По макету 1780-3330: 343×84, r16, отступы 12/14, градиент #007854→#2BB747
 * сверху вниз, поверх — белый орнамент 0→24%. Слева «EVOS Rewards» и
 * баланс с монетой и «глазом», справа «Нажмите для показа QR» и стилизованный код.
 * Тап по карточке открывает шторку QR; тап по «глазу» только прячет цифру.
 * Рисуется только авторизованным — это решает экран, который её ставит.
 */
export const LoyaltyCard = ({ className }: LoyaltyCardProps) => {
  const { t } = useTranslation();
  const user = useSelector(userInfo);
  const { availablePoints, isLoading: isBalanceLoading } = useGetMindboxUserInfo(
    // Не зависит от флага mindbox в /settings: карта с QR показывается всегда,
    // и баланс на ней должен быть настоящим, а не нулём. Флаг решает только
    // списание баллов в корзине.
    user.isAuth
  );
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(readHidden);

  const toggleHidden = () => {
    const next = !hidden;
    setHidden(next);
    try {
      localStorage.setItem(BALANCE_HIDDEN_KEY, next ? '1' : '0');
    } catch {
      // Приватный режим — просто не запомним.
    }
  };

  const handleOpen = () => {
    impactOccuer('soft');
    setOpen(true);
  };

  // Вместо валюты — монета: «160 000 сум» в 28px не помещается рядом с
  // правым блоком. Скрытая цифра — точками.
  const balance = hidden ? '••••••' : availablePoints.toLocaleString('ru-RU');
  const { rowRef, textRef, fontSize } = useFitBalanceFont(isBalanceLoading ? '' : balance);

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={handleOpen}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleOpen();
          }
        }}
        className={cn(
          'relative flex h-[84px] w-full cursor-pointer items-center justify-between gap-2 overflow-hidden rounded-2xl py-3 pl-[14px] pr-[14px] text-white',
          'bg-[linear-gradient(180deg,#007854_0%,#2BB747_100%)]',
          className
        )}
      >
        <LoyaltyOrnament className="pointer-events-none absolute inset-0 h-full w-full" preserveAspectRatio="xMidYMid slice" />

        {/* Узкие телефоны (360px): баланс и подсказка с QR делят ширину, а не
            наезжают друг на друга. Подсказка фиксированной ширины 64px и
            переносится в несколько строк, левая колонка берёт весь остаток —
            туда помещается баланс. Кегль суммы подбирается под ширину
            (useFitBalanceFont), многоточие — только крайний случай. */}
        <div className="relative flex min-w-0 flex-1 flex-col justify-between self-stretch">
          <p className="truncate text-caption-sm-medium text-white/90">{t('loyalty-card')}</p>
          <div ref={rowRef} className="flex min-w-0 items-center gap-1.5">
            {isBalanceLoading ? (
              <Skeleton className="h-7 w-24 rounded-lg bg-white/30" />
            ) : (
              <p
                ref={textRef}
                className="min-w-0 truncate whitespace-nowrap font-inter-tight font-semibold leading-8"
                style={{ fontSize }}
              >
                {balance}
              </p>
            )}
            {/* Монета по макету: слот 20×22 в ряду, сама картинка (с полями)
                25.67×26.87 по центру слота и повёрнута на 25° против часовой
                — так в Figma. За слот она вылезает, место в ряду не меняет. */}
            <span aria-hidden className="relative h-[22px] w-5 shrink-0">
              <img
                src={coinFullImg}
                alt=""
                className="absolute left-1/2 top-1/2 h-[26.87px] w-[25.67px] max-w-none -translate-x-1/2 -translate-y-1/2 -rotate-[25deg]"
              />
            </span>
            {/* Зона нажатия 32×32, а место в ряду — 24px (-mx-1), чтобы
                не отнимать ширину у баланса. */}
            <button
              type="button"
              aria-label={hidden ? t('show') : t('hide')}
              onClick={event => {
                event.stopPropagation();
                toggleHidden();
              }}
              className="-mx-1 flex h-8 w-8 shrink-0 items-center justify-center"
            >
              <EyeIcon className="h-6 w-6" crossed={hidden} />
            </button>
          </div>
        </div>

        <div className="relative flex shrink-0 items-start gap-2">
          <p className="w-16 break-words text-right font-inter-tight text-[10px] font-medium leading-none text-white">
            {t('tap-to-show-qr')}
          </p>
          <LoyaltyQrIcon className="h-[61px] w-[61px] shrink-0" />
        </div>
      </div>

      <LoyaltyQrDrawer open={open} setOpen={setOpen} />
    </>
  );
};
